import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Spinner } from 'react-bootstrap';
import { convertTextToTitleCase } from '../../settings/shared/commonMethods';
import BillingService from '../../services/api/billing';
import AgentService from '../../services/api/agents';
import { API_RESPONSE, default_size } from '../../settings/shared/constants';
import * as d3 from 'd3';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { FiLayers, FiChevronDown, FiSearch, FiX, FiActivity, FiCalendar, FiClock, FiBarChart2, FiSliders, FiUser, FiFolder } from 'react-icons/fi';

// ---------------------------------------------------------------------------
// ProjectSelect — custom searchable dropdown
// ---------------------------------------------------------------------------
const DOT_COLORS = [
  '#0ea5e9', '#8b5cf6', '#10b981', '#f59e0b',
  '#ef4444', '#ec4899', '#06b6d4', '#84cc16',
  '#f97316', '#6366f1',
];

const ProjectSelect = ({
  projects,
  value,
  onChange,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  query,
  onQueryChange,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(query || '');
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    setSearch(query || '');
  }, [query]);

  const enriched = useMemo(
    () =>
      (Array.isArray(projects) ? projects : []).map((p, i) => ({
        id: String(p?.id || p?._id || i),
        createdBy: p?.createdBy,
        name: p?.name || 'Untitled Project',
        color: DOT_COLORS[i % DOT_COLORS.length],
      })),
    [projects],
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return q
      ? enriched.filter((p) => {
        const name = (p.name || '').toLowerCase();
        const id = (p.id || '').toLowerCase();
        const createdBy = (p.createdBy || '').toLowerCase();
        return name.includes(q) || id.includes(q) || createdBy.includes(q);
      })
      : enriched;
  }, [enriched, search]);

  const selected = enriched.find((p) => p.id === String(value)) || null;

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const handleSelect = (proj) => {
    onChange(proj.id);
    setOpen(false);
    setSearch('');
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', minWidth: 240 }}>
      {/* Trigger */}
      <button
        type="button"
        disabled={loading}
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          padding: '6px 10px',
          borderRadius: 8,
          border: `0.5px solid ${open ? '#0ea5e9' : 'rgba(15,23,42,0.18)'}`,
          background: '#fff',
          cursor: loading ? 'default' : 'pointer',
          outline: 'none',
          boxShadow: open ? '0 0 0 3px rgba(14,165,233,0.12)' : 'none',
          transition: 'border-color .15s, box-shadow .15s',
          minHeight: 34,
          gap: 8,
        }}
        onClick={() => !loading && setOpen((v) => !v)}
      >
        {loading ? (
          <>
            <Spinner animation="border" size="sm" style={{ width: 13, height: 13, borderWidth: 2 }} />
            <span style={{ fontSize: 13, color: '#94a3b8', flex: 1, textAlign: 'left' }}>Loading projects…</span>
          </>
        ) : selected ? (
          <>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: selected.color, flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: '#0f172a', flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selected.name}
            </span>
          </>
        ) : (
          <span style={{ fontSize: 13, color: '#94a3b8', flex: 1, textAlign: 'left' }}>Select a project…</span>
        )}
        <FiChevronDown
          size={14}
          style={{ color: '#64748b', flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}
        />
      </button>

      {/* Dropdown */}
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 300,
            background: '#fff',
            border: '0.5px solid rgba(15,23,42,0.15)',
            borderRadius: 10,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            overflow: 'hidden',
          }}
        >
          {/* Search */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '0.5px solid rgba(15,23,42,0.08)' }}>
            <FiSearch size={13} style={{ color: '#94a3b8', flexShrink: 0 }} />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => {
                const val = e.target.value;
                setSearch(val);
                if (typeof onQueryChange === 'function') onQueryChange(val);
              }}
              placeholder="Search projects…"
              style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13, color: '#0f172a', background: 'transparent', padding: 0 }}
            />
            {search && (
              <FiX
                size={12}
                style={{ color: '#94a3b8', cursor: 'pointer', flexShrink: 0 }}
                onClick={() => {
                  setSearch('');
                  if (typeof onQueryChange === 'function') onQueryChange('');
                }}
              />
            )}
          </div>

          {/* List */}
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {filtered.length === 0 && !loading ? (
              <div style={{ padding: '12px', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>No projects found</div>
            ) : (
              filtered.map((proj) => (
                <button
                  key={proj.id}
                  type="button"
                  onClick={() => handleSelect(proj)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    padding: '9px 12px',
                    border: 'none',
                    background: proj.id === String(value) ? 'rgba(14,165,233,0.07)' : 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background .1s',
                  }}
                >
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: proj.color, flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>
                    <span style={{ fontSize: 13, color: '#0f172a', display: 'block', lineHeight: 1.3 }}>{proj.name}</span>
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>{proj.createdBy}</span>
                  </span>
                  {proj.id === String(value) && <span style={{ fontSize: 12, color: '#0ea5e9', fontWeight: 600 }}>✓</span>}
                </button>
              ))
            )}
            {hasMore && (
              <button
                type="button"
                disabled={loadingMore}
                onClick={(e) => {
                  e.stopPropagation();
                  if (typeof onLoadMore === 'function') onLoadMore();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  width: '100%',
                  padding: '10px 12px',
                  border: 'none',
                  borderTop: '0.5px solid rgba(15,23,42,0.08)',
                  background: loadingMore ? '#f8fafc' : '#fff',
                  color: loadingMore ? '#94a3b8' : '#0ea5e9',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: loadingMore ? 'default' : 'pointer',
                }}
              >
                {loadingMore ? (
                  <>
                    <Spinner animation="border" size="sm" style={{ width: 12, height: 12, borderWidth: 2 }} />
                    Loading more…
                  </>
                ) : (
                  'Load more projects'
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// HoverCard
// ---------------------------------------------------------------------------
const HOVER_CARD_OFFSET = 12;
const HOVER_CARD_WIDTH = 176;
const HOVER_CARD_HEIGHT = 76;

const getHoverCardCoords = (wrapEl, clientX, clientY) => {
  if (!wrapEl) return { x: 0, y: 0, flipX: false, flipY: false };
  const rect = wrapEl.getBoundingClientRect();
  const scrollLeft = wrapEl.scrollLeft || 0;
  const scrollTop = wrapEl.scrollTop || 0;
  const x = (clientX ?? 0) - rect.left + scrollLeft;
  const y = (clientY ?? 0) - rect.top + scrollTop;
  const visibleRight = scrollLeft + wrapEl.clientWidth;
  const flipX = x + HOVER_CARD_OFFSET + HOVER_CARD_WIDTH > visibleRight;
  const flipY = y - HOVER_CARD_OFFSET - HOVER_CARD_HEIGHT < scrollTop;
  return { x, y, flipX, flipY };
};

const HoverCard = ({ card }) => (
  <div
    style={{
      position: 'absolute',
      left: card.flipX ? card.x - HOVER_CARD_OFFSET : card.x + HOVER_CARD_OFFSET,
      top: card.flipY ? card.y + HOVER_CARD_OFFSET : card.y - HOVER_CARD_OFFSET,
      transform: card.flipX ? 'translateX(-100%)' : undefined,
      background: '#fff',
      border: '0.5px solid rgba(15,23,42,0.15)',
      borderRadius: 8,
      padding: '8px 11px',
      fontSize: 12,
      lineHeight: 1.5,
      whiteSpace: 'nowrap',
      zIndex: 10,
      pointerEvents: 'none',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <div style={{ fontWeight: 500, color: '#0f172a' }}>{card.title}</div>
    <div style={{ color: '#94a3b8', fontSize: 11 }}>{card.subtitle}</div>
    <div style={{ color: '#0ea5e9', fontWeight: 500, marginTop: 2 }}>{card.value}</div>
  </div>
);

// ---------------------------------------------------------------------------
// Pure date / usage helpers (module scope — stable for hook deps)
// ---------------------------------------------------------------------------
const makeDayKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const makeMonthKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const formatDayLabel = (d) =>
  d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const formatMonthLabel = (d) =>
  d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

const toISODate = (d) => {
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const mapDayUsages = (usages) => {
  const byDate = {};
  (Array.isArray(usages) ? usages : []).forEach((row) => {
    if (!row?.date) return;
    const c = Number(row.credits);
    byDate[row.date] = (byDate[row.date] || 0) + (Number.isFinite(c) ? c : 0);
  });
  const out = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i);
    const key = makeDayKey(d);
    out.push({ key, label: formatDayLabel(d), credits: Math.round((byDate[key] || 0) * 10000) / 10000 });
  }
  return out;
};

const mapMonthUsages = (usages) => {
  const byMonth = {};
  (Array.isArray(usages) ? usages : []).forEach((row) => {
    const key = typeof row?.month === 'string' && row.month ? row.month
      : row?.date ? makeMonthKey(new Date(row.date)) : null;
    if (!key) return;
    const c = Number(row.credits);
    byMonth[key] = (byMonth[key] || 0) + (Number.isFinite(c) ? c : 0);
  });
  const out = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(); d.setMonth(d.getMonth() - i); d.setDate(1);
    const key = makeMonthKey(d);
    out.push({ key, label: formatMonthLabel(d), credits: Math.round((byMonth[key] || 0) * 10000) / 10000 });
  }
  return out;
};

const clampDateRangeBounds = (fromDate, toDate, minSelectableDate, maxSelectableDate) => {
  const from = new Date(fromDate); const to = new Date(toDate);
  const min = new Date(minSelectableDate); const max = new Date(maxSelectableDate);
  let f = Number.isNaN(from.getTime()) ? min : from;
  let t = Number.isNaN(to.getTime()) ? max : to;
  if (f < min) f = min; if (t > max) t = max;
  if (f > t) { const tmp = f; f = t; t = tmp; }
  return { fromDate: toISODate(f), toDate: toISODate(t) };
};

const CUSTOM_BAR_MIN_COUNT = 8;
const CUSTOM_BAR_MAX_WIDTH = 26;
const CHART_HEIGHT = 380;
const CHART_MARGIN = { top: 18, right: 20, bottom: 44, left: 46 };

const getCustomPxPerDay = (len) => {
  const n = Math.max(0, Number(len) || 0);
  if (n >= 120) return 8; if (n >= 90) return 10; if (n >= 60) return 12;
  if (n >= 45) return 14; if (n >= 30) return 18; return 26;
};

const getCustomBarData = (rows) =>
  (Array.isArray(rows) ? rows.filter((d) => Number(d?.credits) > 0) : []);

const getCustomScrollableWidth = (barCount, containerWidth = 0) => {
  const n = Math.max(0, Number(barCount) || 0);
  const sideMargins = 44;
  const pxPerDay = getCustomPxPerDay(n);
  return Math.max(containerWidth || 0, sideMargins + n * pxPerDay);
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
const UsageBilling = ({ billingData }) => {

  const summaryUsage = billingData?.summaryUsage;
  const creditUsage = billingData?.creditUsage;

  const usedCredits = creditUsage?.credits?.used || 0;
  const totalCredits =
    summaryUsage?.totalCredits != null
      ? Number(summaryUsage.totalCredits)
      : creditUsage?.credits?.total || 0;
  const planName = creditUsage?.plan || summaryUsage?.plan || 'BASIC';
  const displayPlanName = planName ? convertTextToTitleCase(planName) : 'N/A';
  const monthlyUsedCredits =
    summaryUsage?.currentMonthCredits != null
      ? Number(summaryUsage.currentMonthCredits)
      : creditUsage?.monthlyUsed != null
      ? Number(creditUsage.monthlyUsed)
      : null;

  const displayMonthlyUsed =
    monthlyUsedCredits != null
      ? monthlyUsedCredits
      : usedCredits > 0
      ? Number((usedCredits / 6).toFixed(2))
      : 0;

  const accountId = useMemo(
    () => (typeof window !== 'undefined' ? localStorage.getItem('accountId') : null),
    [],
  );

  // Scope
  const [usageScope, setUsageScope] = useState('tenant'); // tenant | project
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsLoadingMore, setProjectsLoadingMore] = useState(false);
  const [projectsHasMore, setProjectsHasMore] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [projectQuery, setProjectQuery] = useState('');
  const projectsPageRef = useRef(0);
  const selectedProjectIdRef = useRef('');

  const selectedProject = useMemo(() => {
    const id = String(selectedProjectId || '');
    if (!id) return null;
    const list = Array.isArray(projects) ? projects : [];
    return list.find((p) => String(p?.id || p?._id || '') === id) || null;
  }, [projects, selectedProjectId]);

  // Chart state
  const [usageChartsLoading, setUsageChartsLoading] = useState(true);
  const [dailyUsageData, setDailyUsageData] = useState([]);
  const [customDailyUsageData, setCustomDailyUsageData] = useState([]);
  const [customDailyLoading, setCustomDailyLoading] = useState(false);
  const [monthlyUsageData, setMonthlyUsageData] = useState([]);
  const [chartHoverCard, setChartHoverCard] = useState(null);
  const [chartType, setChartType] = useState('daily');
  const [activeCustomPreset, setActiveCustomPreset] = useState(null);

  const dailyChartWrapRef = useRef(null);
  const dailySvgRef = useRef(null);
  const monthlyChartWrapRef = useRef(null);
  const monthlySvgRef = useRef(null);

  // Project usage
  const [projectUsageLoading, setProjectUsageLoading] = useState(false);
  const [projectDailyUsageData, setProjectDailyUsageData] = useState([]);
  const [projectCustomDailyUsageData, setProjectCustomDailyUsageData] = useState([]);
  const [projectCustomDailyLoading, setProjectCustomDailyLoading] = useState(false);
  const [projectMonthlyUsageData, setProjectMonthlyUsageData] = useState([]);

  const [projectSummaryLoading, setProjectSummaryLoading] = useState(false);
  const [projectSummary, setProjectSummary] = useState(null);

  const projectSummaryDisplay = useMemo(() => {
    const summary = projectSummary?.summaryUsage || projectSummary || {};
    const apiTotal = Number(
      summary?.totalCredits ?? summary?.total ?? summary?.totalUsage ?? summary?.creditsUsed,
    );
    const apiMonth = Number(
      summary?.currentMonthCredits ?? summary?.currentMonth ?? summary?.monthlyCredits ?? summary?.monthlyUsed,
    );

    const fallbackTotal = (Array.isArray(projectDailyUsageData) ? projectDailyUsageData : [])
      .reduce((sum, row) => sum + (Number(row?.credits) || 0), 0);

    const currentMonthKey = makeMonthKey(new Date());
    const fallbackMonthFromDaily = (Array.isArray(projectDailyUsageData) ? projectDailyUsageData : [])
      .filter((row) => typeof row?.key === 'string' && row.key.slice(0, 7) === currentMonthKey)
      .reduce((sum, row) => sum + (Number(row?.credits) || 0), 0);
    const fallbackMonthRow = (Array.isArray(projectMonthlyUsageData) ? projectMonthlyUsageData : [])
      .find((row) => row?.key === currentMonthKey);
    const fallbackMonthFromMonthly = Number(fallbackMonthRow?.credits) || 0;
    const fallbackMonth = fallbackMonthFromMonthly > 0 ? fallbackMonthFromMonthly : fallbackMonthFromDaily;

    return {
      total: Number.isFinite(apiTotal) && apiTotal >= 0 ? apiTotal : fallbackTotal,
      month: Number.isFinite(apiMonth) && apiMonth >= 0 ? apiMonth : fallbackMonth,
    };
  }, [projectSummary, projectDailyUsageData, projectMonthlyUsageData]);

  const minSelectableDate = (() => {
    const oldest = new Date();
    oldest.setMonth(oldest.getMonth() - 5);
    oldest.setDate(1);
    return toISODate(oldest);
  })();
  const maxSelectableDate = toISODate(new Date());

  const [selectedFromDate, setSelectedFromDate] = useState(minSelectableDate);
  const [selectedToDate, setSelectedToDate] = useState(maxSelectableDate);

  const datePickerDisabled = usageScope === 'project' && !selectedProjectId;

  const handleFromDateChange = (value) => {
    if (!value) return;
    setActiveCustomPreset(null);
    const nextFrom = value.format('YYYY-MM-DD');
    setSelectedFromDate(nextFrom);
    if (dayjs(nextFrom).isAfter(dayjs(selectedToDate))) {
      setSelectedToDate(nextFrom);
    }
  };

  const handleToDateChange = (value) => {
    if (!value) return;
    setActiveCustomPreset(null);
    const nextTo = value.format('YYYY-MM-DD');
    setSelectedToDate(nextTo);
    if (dayjs(nextTo).isBefore(dayjs(selectedFromDate))) {
      setSelectedFromDate(nextTo);
    }
  };

  const clampDateRange = (fromDate, toDate) =>
    clampDateRangeBounds(fromDate, toDate, minSelectableDate, maxSelectableDate);

  useEffect(() => {
    const { fromDate, toDate } = clampDateRange(selectedFromDate, selectedToDate);
    if (fromDate !== selectedFromDate) setSelectedFromDate(fromDate);
    if (toDate !== selectedToDate) setSelectedToDate(toDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFromDate, selectedToDate]);

  useEffect(() => {
    selectedProjectIdRef.current = selectedProjectId;
  }, [selectedProjectId]);

  const getProjectSearchPayload = (query) => {
    const q = query.trim();
    return q.length >= 2 ? { name: q } : {};
  };

  const applyProjectsPage = (data, page) => {
    const list = Array.isArray(data?.content) ? data.content : [];
    const isLast = data?.last ?? true;
    const totalPages = data?.totalPages ?? 0;
    projectsPageRef.current = page;
    setProjectsHasMore(!isLast && page < totalPages - 1);
    return list;
  };

  // ── Load projects (search + pagination) ───────────────────────────────────
  useEffect(() => {
    if (usageScope !== 'project') return;
    let cancelled = false;
    const payload = getProjectSearchPayload(projectQuery);

    const t = setTimeout(() => {
      setProjectsLoading(true);
      AgentService.getProject(0, payload, default_size)
        .then((res) => {
          if (cancelled) return;
          const list = applyProjectsPage(res?.projectDTOS, 0);
          setProjects(list);

          const selected = String(selectedProjectIdRef.current || '');
          const stillExists =
            selected &&
            list.some((p) => String(p?.id || p?._id || '') === selected);

          if (!stillExists) {
            setSelectedProjectId(String(list[0]?.id || list[0]?._id || ''));
          }
        })
        .finally(() => { if (!cancelled) setProjectsLoading(false); });
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [usageScope, projectQuery]);

  const loadMoreProjects = () => {
    if (projectsLoadingMore || !projectsHasMore || projectsLoading) return;
    const payload = getProjectSearchPayload(projectQuery);
    const nextPage = projectsPageRef.current + 1;

    setProjectsLoadingMore(true);
    AgentService.getProject(nextPage, payload, default_size)
      .then((res) => {
        const list = applyProjectsPage(res?.projectDTOS, nextPage);
        setProjects((prev) => [...prev, ...list]);
      })
      .finally(() => setProjectsLoadingMore(false));
  };

  // ── Fetch tenant usage: DAILY (for daily tab) ─────────────────────────────
  useEffect(() => {
    if (!accountId) { setUsageChartsLoading(false); return; }
    if (usageScope !== 'tenant') return;
    if (chartType !== 'daily') return;
    let cancelled = false;
    setUsageChartsLoading(true);
    BillingService.getAccountCreditUsage('DAY').catch(() => null)
      .then((dayRes) => {
        if (cancelled) return;
        setDailyUsageData(
          dayRes?.status === API_RESPONSE.SUCCESS && Array.isArray(dayRes.usages)
            ? mapDayUsages(dayRes.usages)
            : mapDayUsages([]),
        );
      })
      .finally(() => { if (!cancelled) setUsageChartsLoading(false); });
    return () => { cancelled = true; };
  }, [accountId, usageScope, chartType]);

  // ── Fetch tenant usage: MONTHLY (for monthly tab) ─────────────────────────
  useEffect(() => {
    if (!accountId) { setUsageChartsLoading(false); return; }
    if (usageScope !== 'tenant') return;
    if (chartType !== 'monthly') return;
    let cancelled = false;
    setUsageChartsLoading(true);
    BillingService.getAccountCreditUsage('MONTH').catch(() => null)
      .then((monthRes) => {
        if (cancelled) return;
        setMonthlyUsageData(
          monthRes?.status === API_RESPONSE.SUCCESS && Array.isArray(monthRes.usages)
            ? mapMonthUsages(monthRes.usages)
            : mapMonthUsages([]),
        );
      })
      .finally(() => { if (!cancelled) setUsageChartsLoading(false); });
    return () => { cancelled = true; };
  }, [accountId, usageScope, chartType]);

  // ── Fetch project usage: DAILY (for daily tab) ────────────────────────────
  useEffect(() => {
    if (usageScope !== 'project' || !accountId || !selectedProjectId) return;
    if (chartType !== 'daily') return;
    let cancelled = false;
    setProjectUsageLoading(true);
    BillingService.getProjectCreditUsage(selectedProjectId, 'DAY').catch(() => null)
      .then((dayRes) => {
        if (cancelled) return;
        setProjectDailyUsageData(
          dayRes?.status === API_RESPONSE.SUCCESS && Array.isArray(dayRes.usages)
            ? mapDayUsages(dayRes.usages)
            : mapDayUsages([]),
        );
      })
      .finally(() => { if (!cancelled) setProjectUsageLoading(false); });
    return () => { cancelled = true; };
  }, [usageScope, accountId, selectedProjectId, chartType]);

  // ── Fetch project usage: MONTHLY (for monthly tab) ────────────────────────
  useEffect(() => {
    if (usageScope !== 'project' || !accountId || !selectedProjectId) return;
    if (chartType !== 'monthly') return;
    let cancelled = false;
    setProjectUsageLoading(true);
    BillingService.getProjectCreditUsage(selectedProjectId, 'MONTH').catch(() => null)
      .then((monthRes) => {
        if (cancelled) return;
        setProjectMonthlyUsageData(
          monthRes?.status === API_RESPONSE.SUCCESS && Array.isArray(monthRes.usages)
            ? mapMonthUsages(monthRes.usages)
            : mapMonthUsages([]),
        );
      })
      .finally(() => { if (!cancelled) setProjectUsageLoading(false); });
    return () => { cancelled = true; };
  }, [usageScope, accountId, selectedProjectId, chartType]);

  // ── Fetch project summary (total + current month) ─────────────────────────
  useEffect(() => {
    if (usageScope !== 'project' || !selectedProjectId) return;
    let cancelled = false;
    setProjectSummaryLoading(true);
    BillingService.getProjectCreditUsageSummary(selectedProjectId)
      .catch(() => null)
      .then((res) => {
        if (cancelled) return;
        if (res?.status !== API_RESPONSE.SUCCESS) {
          setProjectSummary(null);
          return;
        }
        const body = res?.data ?? res;
        const summaryUsage = body?.summaryUsage ?? body;
        setProjectSummary({
          ...res,
          summaryUsage,
          totalCredits: Number(
            summaryUsage?.totalCredits ?? summaryUsage?.total ?? summaryUsage?.totalUsage ?? summaryUsage?.creditsUsed,
          ) || 0,
          currentMonthCredits: Number(
            summaryUsage?.currentMonthCredits ?? summaryUsage?.currentMonth ?? summaryUsage?.monthlyCredits ?? summaryUsage?.monthlyUsed,
          ) || 0,
        });
      })
      .finally(() => { if (!cancelled) setProjectSummaryLoading(false); });
    return () => { cancelled = true; };
  }, [usageScope, selectedProjectId]);

  // ── Custom range: auto-fetch when dates / scope / project change ──────────
  useEffect(() => {
    if (chartType !== 'custom') return;
    if (!accountId) return;
    if (usageScope === 'project' && !selectedProjectId) return;

    let cancelled = false;
    const { fromDate, toDate } = clampDateRange(selectedFromDate, selectedToDate);
    const setLoading = usageScope === 'project' ? setProjectCustomDailyLoading : setCustomDailyLoading;
    const setData = usageScope === 'project' ? setProjectCustomDailyUsageData : setCustomDailyUsageData;

    const timer = setTimeout(() => {
      setLoading(true);
      const req =
        usageScope === 'project'
          ? BillingService.getProjectCreditUsage(selectedProjectId, 'DAY', { startDate: fromDate, endDate: toDate })
          : BillingService.getAccountCreditUsage('DAY', { startDate: fromDate, endDate: toDate });

      req
        .catch(() => null)
        .then((res) => {
          if (cancelled) return;
          if (res?.status === API_RESPONSE.SUCCESS && Array.isArray(res.usages)) {
            const byDate = {};
            res.usages.forEach((row) => {
              if (!row?.date) return;
              const c = Number(row.credits);
              byDate[row.date] = (byDate[row.date] || 0) + (Number.isFinite(c) ? c : 0);
            });
            const out = [];
            const start = new Date(fromDate);
            const end = new Date(toDate);
            for (const d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
              const key = makeDayKey(d);
              out.push({
                key,
                label: formatDayLabel(new Date(d)),
                credits: Math.round((byDate[key] || 0) * 10000) / 10000,
              });
            }
            setData(out);
          } else {
            setData([]);
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 280);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartType, accountId, usageScope, selectedProjectId, selectedFromDate, selectedToDate]);

  const applyCustomPreset = (preset) => {
    const today = dayjs(maxSelectableDate);
    const min = dayjs(minSelectableDate);
    let from = today;
    if (preset === '7d') from = today.subtract(6, 'day');
    else if (preset === '30d') from = today.subtract(29, 'day');
    else if (preset === 'month') from = today.startOf('month');
    if (from.isBefore(min)) from = min;
    const fromStr = from.format('YYYY-MM-DD');
    const toStr = today.format('YYYY-MM-DD');
    setActiveCustomPreset(preset);
    setSelectedFromDate(fromStr);
    setSelectedToDate(toStr);
  };

  const periodHint =
    chartType === 'daily' ? 'Last 14 days'
    : chartType === 'monthly' ? 'Last 6 months'
    : `${selectedFromDate} – ${selectedToDate}`;

  const showProjectStats = usageScope === 'project';
  const sidebarTotalUsage = projectSummaryLoading
    ? '…'
    : projectSummaryDisplay.total.toFixed(2);
  const sidebarMonthUsage = projectSummaryLoading
    ? '…'
    : projectSummaryDisplay.month.toFixed(2);

  const chartScopeLabel = usageScope === 'project'
    ? (selectedProject?.name || 'Project Usage')
    : 'Account usage';

  const renderUsageChart = () => {
    if (usageScope === 'tenant' && usageChartsLoading && chartType !== 'custom') {
      return <div className="ub-chart-empty"><Spinner animation="border" size="sm" style={{ marginRight: 8 }} />Loading usage…</div>;
    }
    if (usageScope === 'project' && projectUsageLoading && chartType !== 'custom') {
      return <div className="ub-chart-empty"><Spinner animation="border" size="sm" style={{ marginRight: 8 }} />Loading project usage…</div>;
    }
    if (chartType === 'custom' && (usageScope === 'project' ? projectCustomDailyLoading : customDailyLoading)) {
      return <div className="ub-chart-empty"><Spinner animation="border" size="sm" style={{ marginRight: 8 }} />Updating chart…</div>;
    }
    if (usageScope === 'project' && !selectedProjectId) {
      return <div className="ub-chart-empty">Select a project to view usage.</div>;
    }
    if (chartType === 'daily') {
      const data = usageScope === 'project' ? projectDailyUsageData : dailyUsageData;
      if (!data.length) return <div className="ub-chart-empty">No daily data yet.</div>;
      return (
        <div
          className="ub-chart-wrap"
          ref={dailyChartWrapRef}
          onMouseLeave={handleHideHoverCard}
        >
          <svg ref={dailySvgRef} className="ub-chart-svg" role="img" aria-label="Daily usage chart" />
          {chartHoverCard?.chart === 'daily' && <HoverCard card={chartHoverCard} />}
        </div>
      );
    }
    if (chartType === 'custom') {
      const raw = usageScope === 'project' ? projectCustomDailyUsageData : customDailyUsageData;
      const data = getCustomBarData(raw);
      if (!data.length) return <div className="ub-chart-empty">No usage in this date range.</div>;
      const fewBars = data.length < CUSTOM_BAR_MIN_COUNT;
      return (
        <div
          className={`ub-chart-wrap${fewBars ? '' : ' is-scrollable'}`}
          ref={dailyChartWrapRef}
          onMouseLeave={handleHideHoverCard}
        >
          <svg ref={dailySvgRef} className="ub-chart-svg" role="img" aria-label="Custom usage chart" />
          {chartHoverCard?.chart === 'daily' && <HoverCard card={chartHoverCard} />}
        </div>
      );
    }
    const data = usageScope === 'project' ? projectMonthlyUsageData : monthlyUsageData;
    if (!data.length) return <div className="ub-chart-empty">No monthly data yet.</div>;
    return (
      <div className="ub-chart-wrap" ref={monthlyChartWrapRef} onMouseLeave={handleHideHoverCard}>
        <svg ref={monthlySvgRef} className="ub-chart-svg" role="img" aria-label="Monthly usage chart" />
        {chartHoverCard?.chart === 'monthly' && <HoverCard card={chartHoverCard} />}
      </div>
    );
  };

  const formatChartCredits = (n) =>
    typeof n === 'number' && !Number.isInteger(n) ? n.toFixed(2) : String(n);

  // ── Hover handlers ────────────────────────────────────────────────────────
  const handleShowHoverCard = (wrapEl, event, payload) => {
    if (!wrapEl) return;
    const { x, y, flipX, flipY } = getHoverCardCoords(wrapEl, event?.clientX, event?.clientY);
    setChartHoverCard({
      chart: payload.chart,
      x, y, flipX, flipY,
      title: payload.title, subtitle: payload.subtitle, value: payload.value,
    });
  };
  const handleMoveHoverCard = (wrapEl, event) => {
    setChartHoverCard((prev) => {
      if (!prev || !wrapEl) return prev;
      const { x, y, flipX, flipY } = getHoverCardCoords(wrapEl, event?.clientX, event?.clientY);
      return { ...prev, x, y, flipX, flipY };
    });
  };
  const handleHideHoverCard = () => setChartHoverCard(null);

  // ── D3: Daily / Custom chart (tenant + project) ───────────────────────────
  useEffect(() => {
    const wrap = dailyChartWrapRef.current; const svgEl = dailySvgRef.current;
    if (!wrap || !svgEl) return;

    const scopedDaily = usageScope === 'project' ? projectDailyUsageData : dailyUsageData;
    const scopedCustom = usageScope === 'project' ? projectCustomDailyUsageData : customDailyUsageData;
    const sourceRaw = chartType === 'custom' ? scopedCustom : scopedDaily;
    const source = chartType === 'custom' ? getCustomBarData(sourceRaw) : sourceRaw;
    const data = Array.isArray(source)
      ? source.map((d) => ({ ...d, credits: typeof d.credits === 'number' ? d.credits : Number(d.credits) || 0 }))
      : [];

    const containerWidth = Math.max(wrap.clientWidth || 560, 320);
    const width = chartType === 'custom' && data.length >= CUSTOM_BAR_MIN_COUNT
      ? getCustomScrollableWidth(data.length, containerWidth)
      : containerWidth;
    const height = CHART_HEIGHT;
    const margin = CHART_MARGIN;

    const svg = d3.select(svgEl);
    svg
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMinYMin meet');
    svg.selectAll('*').remove();
    if (data.length === 0) return;

    const maxY = Math.max(d3.max(data, (d) => d.credits) || 0, 1);
    const y = d3.scaleLinear().domain([0, maxY]).nice().range([height - margin.bottom, margin.top]);

    svg.append('g')
      .attr('class', 'ub-chart-grid')
      .attr('transform', `translate(${margin.left},0)`)
      .call(
        d3.axisLeft(y)
          .ticks(5)
          .tickSize(-(width - margin.left - margin.right))
          .tickFormat(''),
      )
      .call((g) => g.selectAll('line').attr('stroke', 'rgba(15,23,42,0.06)'))
      .call((g) => g.select('.domain').remove());

    svg.append('g').attr('transform', `translate(${margin.left},0)`)
      .call(d3.axisLeft(y).ticks(5).tickSizeOuter(0))
      .call((g) => g.selectAll('text').attr('fill', '#64748b').attr('font-size', 10))
      .call((g) => g.selectAll('path, line').attr('stroke', 'rgba(15,23,42,0.12)'));

    const accent = '#0ea5e9';

    if (chartType === 'custom') {
      const x = d3.scaleBand().domain(data.map((d) => d.key)).range([margin.left, width - margin.right]).padding(0.35);
      const zeroY = y(0);
      const bandWidth = x.bandwidth();
      const barWidth = data.length < CUSTOM_BAR_MIN_COUNT
        ? Math.min(bandWidth, CUSTOM_BAR_MAX_WIDTH)
        : bandWidth;
      const showBarHover = (event, d) => {
        if (Number(d.credits) === 0) return;
        handleShowHoverCard(wrap, event, {
          chart: 'daily',
          title: d.label,
          subtitle: 'Daily usage',
          value: `${formatChartCredits(d.credits)} credits`,
        });
      };
      svg.append('g').attr('class', 'ub-chart-hit-layer').selectAll('rect').data(data, (d) => d.key).join('rect')
        .attr('x', (d) => x(d.key))
        .attr('width', bandWidth)
        .attr('y', margin.top)
        .attr('height', height - margin.top - margin.bottom)
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mouseenter', showBarHover)
        .on('mousemove', (event) => handleMoveHoverCard(wrap, event));
      svg.append('g').attr('class', 'ub-chart-bar-layer').selectAll('rect').data(data, (d) => d.key).join('rect')
        .attr('x', (d) => x(d.key) + (bandWidth - barWidth) / 2)
        .attr('width', barWidth)
        .attr('y', (d) => y(d.credits)).attr('height', (d) => Math.max(zeroY - y(d.credits), 2))
        .attr('rx', 6).attr('fill', accent)
        .attr('pointer-events', 'none');
      const labelStep = Math.max(1, Math.ceil(data.length / 10));
      const tickKeys = data.filter((_, i) => i % labelStep === 0).map((d) => d.key);
      svg.append('g').attr('transform', `translate(0,${height - margin.bottom})`)
        .call(d3.axisBottom(x).tickValues(tickKeys).tickFormat((k) => data.find((d) => d.key === k)?.label || ''))
        .call((g) => g.selectAll('text').attr('fill', '#64748b').attr('font-size', 10))
        .call((g) => g.selectAll('path, line').attr('stroke', 'rgba(15,23,42,0.12)'));
      return;
    }

    const x = d3.scalePoint().domain(data.map((d) => d.key)).range([margin.left, width - margin.right]).padding(0.5);
    const line = d3.line().x((d) => x(d.key)).y((d) => y(d.credits)).curve(d3.curveMonotoneX);
    const area = d3.area().x((d) => x(d.key)).y0(y(0)).y1((d) => y(d.credits)).curve(d3.curveMonotoneX);

    svg.append('path').datum(data).attr('fill', 'rgba(14,165,233,0.18)').attr('stroke', 'none').attr('d', area);
    svg.append('path').datum(data).attr('fill', 'none').attr('stroke', accent).attr('stroke-width', 2.5)
      .attr('stroke-linecap', 'round').attr('stroke-linejoin', 'round').attr('d', line);
    svg.append('g').selectAll('circle').data(data, (d) => d.key).join('circle')
      .attr('cx', (d) => x(d.key)).attr('cy', (d) => y(d.credits)).attr('r', 4)
      .attr('fill', accent).style('cursor', 'pointer')
      .on('mouseenter', (event, d) => handleShowHoverCard(wrap, event, { chart: 'daily', title: d.label, subtitle: 'Daily usage', value: `${formatChartCredits(d.credits)} credits` }))
      .on('mousemove', (event) => handleMoveHoverCard(wrap, event));
    const labelStep = Math.max(1, Math.ceil(data.length / 10));
    const tickKeys = data.filter((_, i) => i % labelStep === 0).map((d) => d.key);
    svg.append('g').attr('transform', `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(x).tickValues(tickKeys).tickFormat((k) => data.find((d) => d.key === k)?.label || ''))
      .call((g) => g.selectAll('text').attr('fill', '#64748b').attr('font-size', 10))
      .call((g) => g.selectAll('path, line').attr('stroke', 'rgba(15,23,42,0.12)'));
  }, [usageScope, dailyUsageData, customDailyUsageData, projectDailyUsageData, projectCustomDailyUsageData, chartType]);

  // ── D3: Monthly chart (tenant + project) ──────────────────────────────────
  useEffect(() => {
    const wrap = monthlyChartWrapRef.current; const svgEl = monthlySvgRef.current;
    if (!wrap || !svgEl) return;
    const base = usageScope === 'project'
      ? (Array.isArray(projectMonthlyUsageData) ? projectMonthlyUsageData : [])
      : (Array.isArray(monthlyUsageData) ? monthlyUsageData : []);
    const { fromDate, toDate } = clampDateRangeBounds(selectedFromDate, selectedToDate, minSelectableDate, maxSelectableDate);
    const startKey = fromDate.slice(0, 7); const endKey = toDate.slice(0, 7);
    const source = chartType === 'custom' ? base.filter((m) => m.key >= startKey && m.key <= endKey) : base;
    const data = source.map((d) => ({ ...d, credits: typeof d.credits === 'number' ? d.credits : Number(d.credits) || 0 }));

    const width = Math.max(wrap.clientWidth || 560, 320);
    const height = CHART_HEIGHT;
    const margin = CHART_MARGIN;
    const svg = d3.select(svgEl);
    svg
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMinYMin meet');
    svg.selectAll('*').remove();
    if (data.length === 0) return;

    const x = d3.scalePoint().domain(data.map((d) => d.key)).range([margin.left, width - margin.right]).padding(0.5);
    const maxY = Math.max(d3.max(data, (d) => d.credits) || 0, 1);
    const y = d3.scaleLinear().domain([0, maxY]).nice().range([height - margin.bottom, margin.top]);

    svg.append('g')
      .attr('class', 'ub-chart-grid')
      .attr('transform', `translate(${margin.left},0)`)
      .call(
        d3.axisLeft(y)
          .ticks(5)
          .tickSize(-(width - margin.left - margin.right))
          .tickFormat(''),
      )
      .call((g) => g.selectAll('line').attr('stroke', 'rgba(15,23,42,0.06)'))
      .call((g) => g.select('.domain').remove());

    svg.append('g').attr('transform', `translate(${margin.left},0)`)
      .call(d3.axisLeft(y).ticks(5).tickSizeOuter(0))
      .call((g) => g.selectAll('text').attr('fill', '#64748b').attr('font-size', 10))
      .call((g) => g.selectAll('path, line').attr('stroke', 'rgba(15,23,42,0.12)'));

    const accent = usageScope === 'project' ? '#f97316' : '#8b5cf6';
    const line = d3.line().x((d) => x(d.key)).y((d) => y(d.credits)).curve(d3.curveMonotoneX);
    const area = d3.area().x((d) => x(d.key)).y0(y(0)).y1((d) => y(d.credits)).curve(d3.curveMonotoneX);

    svg.append('path').datum(data).attr('fill', usageScope === 'project' ? 'rgba(249,115,22,0.18)' : 'rgba(139,92,246,0.18)').attr('stroke', 'none').attr('d', area);
    svg.append('path').datum(data).attr('fill', 'none').attr('stroke', accent).attr('stroke-width', 2.5)
      .attr('stroke-linecap', 'round').attr('stroke-linejoin', 'round').attr('d', line);
    svg.append('g').selectAll('circle').data(data, (d) => d.key).join('circle')
      .attr('cx', (d) => x(d.key)).attr('cy', (d) => y(d.credits)).attr('r', 4)
      .attr('fill', accent).style('cursor', 'pointer')
      .on('mouseenter', (event, d) => handleShowHoverCard(wrap, event, { chart: 'monthly', title: d.label, subtitle: 'Monthly usage', value: `${formatChartCredits(d.credits)} credits` }))
      .on('mousemove', (event) => handleMoveHoverCard(wrap, event));
    svg.append('g').selectAll('text').data(data).join('text')
      .attr('x', (d) => x(d.key)).attr('y', height - 12)
      .attr('text-anchor', 'middle').attr('fill', '#64748b').attr('font-size', 10)
      .text((d) => d.label);
  }, [usageScope, monthlyUsageData, projectMonthlyUsageData, chartType, selectedFromDate, selectedToDate, minSelectableDate, maxSelectableDate]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={styles.container}>

      {/* Billing overview */}
      <div className="bo-overview">
        <p className="bo-overview-heading">Billing overview</p>
        <div className="bo-overview-card">
          <div className="bo-overview-item bo-overview-item--plan">
            <div className="bo-overview-icon bo-overview-icon--plan"><FiLayers size={16} /></div>
            <div className="bo-overview-content">
              <span className="bo-overview-label">Plan</span>
              <span className="bo-overview-value bo-overview-value--plan" title={displayPlanName}>
                {displayPlanName}
              </span>
            </div>
          </div>
          <div className="bo-overview-item bo-overview-item--total">
            <div className="bo-overview-icon bo-overview-icon--total"><FiActivity size={16} /></div>
            <div className="bo-overview-content">
              <span className="bo-overview-label">Total credits</span>
              <span className="bo-overview-value">
                {typeof totalCredits === 'number' ? totalCredits.toFixed(2) : totalCredits}
                <span className="bo-overview-unit">credits</span>
              </span>
            </div>
          </div>
          <div className="bo-overview-item bo-overview-item--month">
            <div className="bo-overview-icon bo-overview-icon--month"><FiCalendar size={16} /></div>
            <div className="bo-overview-content">
              <span className="bo-overview-label">This month</span>
              <span className="bo-overview-value">
                {typeof displayMonthlyUsed === 'number' ? displayMonthlyUsed.toFixed(2) : displayMonthlyUsed}
                <span className="bo-overview-unit">credits</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Usage Summary */}
      <div className="ub-dashboard">
        <div className="ub-dashboard-header">
          <div className="ub-dashboard-header-left">
            <div className="ub-dashboard-header-icon"><FiBarChart2 size={16} /></div>
            <div>
              <p className="ub-dashboard-title">Usage Summary</p>
              <p className="ub-dashboard-subtitle">Track credit consumption over time</p>
            </div>
          </div>
          <div className="ub-dashboard-header-controls">
            <div className="ub-scope-segment" role="radiogroup" aria-label="Usage scope">
              <div
                className="ub-scope-segment-slider"
                aria-hidden="true"
                style={{ transform: usageScope === 'project' ? 'translateX(100%)' : 'translateX(0%)' }}
              />
              <label className={`ub-scope-segment-btn${usageScope === 'tenant' ? ' is-active' : ''}`}>
                <input
                  type="radio"
                  name="usageScope"
                  value="tenant"
                  checked={usageScope === 'tenant'}
                  onChange={() => { setUsageScope('tenant'); setChartHoverCard(null); setChartType('daily'); }}
                />
                <span className="ub-scope-segment-icon"><FiUser size={14} strokeWidth={2.25} /></span>
                <span className="ub-scope-segment-label">Account</span>
              </label>
              <label className={`ub-scope-segment-btn${usageScope === 'project' ? ' is-active' : ''}`}>
                <input
                  type="radio"
                  name="usageScope"
                  value="project"
                  checked={usageScope === 'project'}
                  onChange={() => { setUsageScope('project'); setChartHoverCard(null); }}
                />
                <span className="ub-scope-segment-icon"><FiFolder size={14} strokeWidth={2.25} /></span>
                <span className="ub-scope-segment-label">Project</span>
              </label>
            </div>
            {usageScope === 'project' && (
              <div className="ub-header-project-select">
                <ProjectSelect
                  projects={projects}
                  value={selectedProjectId}
                  onChange={setSelectedProjectId}
                  loading={projectsLoading}
                  loadingMore={projectsLoadingMore}
                  hasMore={projectsHasMore}
                  onLoadMore={loadMoreProjects}
                  query={projectQuery}
                  onQueryChange={setProjectQuery}
                />
              </div>
            )}
          </div>
        </div>

        <div className="ub-dashboard-body">
          <aside className="ub-sidebar">
            {showProjectStats && (
              <>
                <div className="ub-stat-card ub-stat-card--total">
                  <div className="ub-stat-icon"><FiActivity size={15} /></div>
                  <div className="ub-stat-content">
                    <span className="ub-stat-label">Total usage</span>
                    <span className="ub-stat-value">
                      {selectedProjectId ? sidebarTotalUsage : '—'}
                      <span className="ub-stat-unit">credits</span>
                    </span>
                  </div>
                </div>
                <div className="ub-stat-card ub-stat-card--month">
                  <div className="ub-stat-icon"><FiCalendar size={15} /></div>
                  <div className="ub-stat-content">
                    <span className="ub-stat-label">This month</span>
                    <span className="ub-stat-value">
                      {selectedProjectId ? sidebarMonthUsage : '—'}
                      <span className="ub-stat-unit">credits</span>
                    </span>
                  </div>
                </div>
              </>
            )}

            <div className="ub-sidebar-section">
              <span className="ub-sidebar-section-label">Period</span>
              <div className="ub-period-list" role="tablist" aria-label="Usage period">
                {[
                  { id: 'daily', label: 'Daily', hint: '14 days', icon: FiClock },
                  { id: 'monthly', label: 'Monthly', hint: '6 months', icon: FiBarChart2 },
                  { id: 'custom', label: 'Custom', hint: 'Date range', icon: FiSliders },
                ].map((t) => {
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      role="tab"
                      aria-selected={chartType === t.id}
                      className={`ub-period-item${chartType === t.id ? ' is-active' : ''}`}
                      onClick={() => {
                        setChartType(t.id);
                        setChartHoverCard(null);
                        if (t.id === 'custom' && chartType !== 'custom' && !activeCustomPreset) {
                          applyCustomPreset('month');
                        }
                      }}
                    >
                      <span className="ub-period-icon"><Icon size={14} /></span>
                      <span className="ub-period-text">
                        <span className="ub-period-label">{t.label}</span>
                        <span className="ub-period-hint">{t.hint}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {chartType === 'custom' && (
              <div className="ub-sidebar-section ub-custom-range">
                <span className="ub-sidebar-section-label">Date range</span>
                <div className="ub-date-fields">
                  <label className="ub-date-field">
                    <span className="ub-date-field-label">From</span>
                    <DatePicker
                      value={dayjs(selectedFromDate)}
                      onChange={handleFromDateChange}
                      disabledDate={(current) => {
                        if (!current) return false;
                        return current.isBefore(dayjs(minSelectableDate).startOf('day')) ||
                          current.isAfter(dayjs(selectedToDate).endOf('day')) ||
                          current.isAfter(dayjs(maxSelectableDate).endOf('day'));
                      }}
                      allowClear={false}
                      size="small"
                      format="MMM D, YYYY"
                      style={{ width: '100%' }}
                      disabled={datePickerDisabled}
                    />
                  </label>
                  <label className="ub-date-field">
                    <span className="ub-date-field-label">To</span>
                    <DatePicker
                      value={dayjs(selectedToDate)}
                      onChange={handleToDateChange}
                      disabledDate={(current) => {
                        if (!current) return false;
                        return current.isBefore(dayjs(selectedFromDate).startOf('day')) ||
                          current.isBefore(dayjs(minSelectableDate).startOf('day')) ||
                          current.isAfter(dayjs(maxSelectableDate).endOf('day'));
                      }}
                      allowClear={false}
                      size="small"
                      format="MMM D, YYYY"
                      style={{ width: '100%' }}
                      disabled={datePickerDisabled}
                    />
                  </label>
                </div>
              </div>
            )}
          </aside>

          <div className="ub-chart-panel">
            <div className="ub-chart-panel-header">
              <div className="ub-chart-scope">
                <span className="ub-chart-scope-dot" style={{
                  background: usageScope === 'project' && selectedProject
                    ? (DOT_COLORS[projects.findIndex((p) => String(p?.id || p?._id) === selectedProjectId) % DOT_COLORS.length] || '#f97316')
                    : '#3b82f6',
                }} />
                <span className="ub-chart-scope-name">{chartScopeLabel}</span>
              </div>
              <span className="ub-chart-period-badge">{periodHint}</span>
            </div>
            <div className="ub-chart-canvas">
              {renderUsageChart()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Styles (minimal — main styles in billing.css)
// ---------------------------------------------------------------------------
const styles = {
  container: { padding: 10, fontSize: 14 },
};

export default UsageBilling;

